import { EAvailableLanguageCodes, EEvaluationType } from '@hubertai/screening-question-types';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';

export const LANGUAGES: { code: TAvailableLanguageCodes; flag: string }[] = [
  { code: EAvailableLanguageCodes.enGB, flag: '🇬🇧' },
  { code: EAvailableLanguageCodes.enUS, flag: '🇺🇸' },
  { code: EAvailableLanguageCodes.en001, flag: '🌍' },
  { code: EAvailableLanguageCodes.svSE, flag: '🇸🇪' },
  { code: EAvailableLanguageCodes.noNO, flag: '🇳🇴' },
  { code: EAvailableLanguageCodes.nnNO, flag: '🇳🇴' },
  { code: EAvailableLanguageCodes.daDK, flag: '🇩🇰' },
  { code: EAvailableLanguageCodes.deDE, flag: '🇩🇪' },
  { code: EAvailableLanguageCodes.frFR, flag: '🇫🇷' },
  { code: EAvailableLanguageCodes.esES, flag: '🇪🇸' },
  { code: EAvailableLanguageCodes.esMX, flag: '🇲🇽' },
  { code: EAvailableLanguageCodes.itIT, flag: '🇮🇹' },
  { code: EAvailableLanguageCodes.plPL, flag: '🇵🇱' },
  { code: EAvailableLanguageCodes.nlNL, flag: '🇳🇱' },
  { code: EAvailableLanguageCodes.fiFI, flag: '🇫🇮' },
  { code: EAvailableLanguageCodes.ptPT, flag: '🇵🇹' },
  { code: EAvailableLanguageCodes.jaJP, flag: '🇯🇵' },
  { code: EAvailableLanguageCodes.ruRU, flag: '🇷🇺' },
  { code: EAvailableLanguageCodes.ukUA, flag: '🇺🇦' },
  { code: EAvailableLanguageCodes.trTR, flag: '🇹🇷' },
  { code: EAvailableLanguageCodes.zhCN, flag: '🇨🇳' },
  { code: EAvailableLanguageCodes.koKR, flag: '🇰🇷' },
  { code: EAvailableLanguageCodes.heIL, flag: '🇮🇱' },
  { code: EAvailableLanguageCodes.ar001, flag: '🌍' },
  { code: EAvailableLanguageCodes.elGR, flag: '🇬🇷' },
  { code: EAvailableLanguageCodes.elGRLatn, flag: '🇬🇷' },
  { code: EAvailableLanguageCodes.hrHR, flag: '🇭🇷' },
  { code: EAvailableLanguageCodes.csCZ, flag: '🇨🇿' },
  { code: EAvailableLanguageCodes.huHU, flag: '🇭🇺' },
  { code: EAvailableLanguageCodes.roRO, flag: '🇷🇴' },
  { code: EAvailableLanguageCodes.skSK, flag: '🇸🇰' },
  { code: EAvailableLanguageCodes.sqAL, flag: '🇦🇱' },
  { code: EAvailableLanguageCodes.afZA, flag: '🇿🇦' },
  { code: EAvailableLanguageCodes.yi001, flag: '🕍' },
  { code: EAvailableLanguageCodes.ar001Latn, flag: '🌍' },
];

// A language's label is its own curated `languages.*` entry, so it reads in the manager's UI locale.
// Bare codes ('sv', from an intake summary's `summary_<code>` key) resolve to the product's variant
// of that language ('sv-se'), which keeps both code shapes on one key set.
export const languageLabelKey = (code: string): string => {
  const normalized = (typeof code === 'string' ? code : '').trim().toLowerCase();
  if (!normalized) return 'languages.unknown';
  const match = LANGUAGES.find((l) => l.code === normalized || l.code.startsWith(`${normalized}-`));
  return `languages.${match?.code ?? normalized}`;
};
// Colors reuse the app-wide --color-evaluation-* tokens (src/index.css) so an option's evaluation
// reads the same here as it does in the template builder and interview scoring views.
export const EVALUATION_TYPE_CONFIG: Record<
  EEvaluationType,
  { labelKey: string; textColor: string; bgColor: string; borderColor: string; dot: string }
> = {
  [EEvaluationType.Info]: {
    labelKey: `evaluationType.${EEvaluationType.Info}`,
    textColor: 'text-evaluation-info',
    bgColor: 'bg-evaluation-info/10',
    borderColor: 'border-evaluation-info/30',
    dot: 'bg-evaluation-info',
  },
  [EEvaluationType.Warning]: {
    labelKey: `evaluationType.${EEvaluationType.Warning}`,
    textColor: 'text-evaluation-warning',
    bgColor: 'bg-evaluation-warning/10',
    borderColor: 'border-evaluation-warning/30',
    dot: 'bg-evaluation-warning',
  },
  [EEvaluationType.Bonus]: {
    labelKey: `evaluationType.${EEvaluationType.Bonus}`,
    textColor: 'text-evaluation-bonus',
    bgColor: 'bg-evaluation-bonus/10',
    borderColor: 'border-evaluation-bonus/30',
    dot: 'bg-evaluation-bonus',
  },
  [EEvaluationType.Disqualified]: {
    labelKey: `evaluationType.${EEvaluationType.Disqualified}`,
    textColor: 'text-evaluation-disqualified',
    bgColor: 'bg-evaluation-disqualified/10',
    borderColor: 'border-evaluation-disqualified/30',
    dot: 'bg-evaluation-disqualified',
  },
  [EEvaluationType.Qualified]: {
    labelKey: `evaluationType.${EEvaluationType.Qualified}`,
    textColor: 'text-evaluation-qualified',
    bgColor: 'bg-evaluation-qualified/10',
    borderColor: 'border-evaluation-qualified/30',
    dot: 'bg-evaluation-qualified',
  },
  [EEvaluationType.AutoQualified]: {
    labelKey: `evaluationType.${EEvaluationType.AutoQualified}`,
    textColor: 'text-evaluation-qualified',
    bgColor: 'bg-evaluation-qualified/10',
    borderColor: 'border-evaluation-qualified/30',
    dot: 'bg-evaluation-qualified',
  },
  [EEvaluationType.Point]: {
    labelKey: `evaluationType.${EEvaluationType.Point}`,
    textColor: 'text-evaluation-point',
    bgColor: 'bg-evaluation-point/10',
    borderColor: 'border-evaluation-point/30',
    dot: 'bg-evaluation-point',
  },
  [EEvaluationType.Weighted]: {
    labelKey: `evaluationType.${EEvaluationType.Weighted}`,
    textColor: 'text-evaluation-point',
    bgColor: 'bg-evaluation-point/10',
    borderColor: 'border-evaluation-point/30',
    dot: 'bg-evaluation-point',
  },
};
export const COLOR_MAP: Record<string, { accent: string; bg: string; border: string }> = {
  violet: { accent: 'bg-violet-500', bg: 'bg-violet-50', border: 'border-violet-200' },
  indigo: { accent: 'bg-[#7C85F8]', bg: 'bg-[#EEF0FF]', border: 'border-[#D4D8FF]' },
  sky: { accent: 'bg-sky-500', bg: 'bg-sky-50', border: 'border-sky-200' },
  amber: { accent: 'bg-amber-400', bg: 'bg-amber-50', border: 'border-amber-200' },
  emerald: { accent: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  pink: { accent: 'bg-pink-500', bg: 'bg-pink-50', border: 'border-pink-200' },
  slate: { accent: 'bg-slate-400', bg: 'bg-slate-100', border: 'border-slate-200' },
};
